import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Heart, Share2, MapPin, Eye, Star, MessageCircle, ShoppingCart, ChevronLeft, ChevronRight, Shield, User, Sparkles, Coins, Package, Truck, Video, Play, ImageIcon, Pencil, Trash2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import api, { API_URL } from '../lib/api';

const conditionMap = { new: 'Novo', used: 'Usado' };
const typeMap = { physical: 'Físico', digital: 'Digital', service: 'Serviço' };
const deliveryMap = {
  seller_ships: { icon: Package, label: 'Vendedor envia' },
  buyer_picks_up: { icon: Truck, label: 'Comprador retira' },
  local_only: { icon: MapPin, label: 'Retirar no local' },
  chat_arrange: { icon: MessageCircle, label: 'Combinar pelo chat' },
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [seller, setSeller] = useState(null);
  const [sellerReviews, setSellerReviews] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isPromoted = product?.is_promoted && product?.promoted_until && new Date(product.promoted_until) > new Date();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data.product);
        setSeller(data.seller);
        if (data.seller?._id) {
          api.get(`/reviews/user/${data.seller._id}`).then(r => setSellerReviews(r.data.reviews || [])).catch(() => {});
        }
      } catch {
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleBuy = () => {
    if (!user) { navigate('/login'); return; }
    navigate(`/checkout/${id}`);
  };

  const handleChat = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const { data } = await api.post('/chat/conversations', { user_id: seller._id, product_id: id });
      navigate(`/chat/${data.conversation._id}`);
    } catch {
      alert('Erro ao iniciar conversa');
    }
  };

  const handlePromote = async () => {
    if (!user) { navigate('/login'); return; }
    setPromoting(true);
    try {
      const { data } = await api.post(`/products/${id}/promote`);
      alert(data.message);
      const res = await api.get(`/products/${id}`);
      setProduct(res.data.product);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao destacar produto');
    } finally {
      setPromoting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/products/${id}`);
      navigate('/dashboard?tab=listings');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao remover anúncio');
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
    }
  };

  const getImageUrl = (img) => img?.startsWith('http') ? img : `${API_URL}/api/files/${img}`;
  const getVideoUrl = (v) => v?.startsWith('http') ? v : `${API_URL}/api/files/${v}`;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="aspect-square bg-muted rounded-xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-muted rounded w-3/4 animate-pulse" />
            <div className="h-10 bg-muted rounded w-1/3 animate-pulse" />
            <div className="h-32 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const images = product.images?.length ? product.images : [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-foreground">Início</Link>
        <span>/</span>
        <Link to="/products" className="hover:text-foreground">Produtos</Link>
        <span>/</span>
        <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-foreground">{product.category}</Link>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Media (Images + Video) */}
        <div>
          {/* Tabs: Fotos / Vídeo */}
          {product.video && images.length > 0 && (
            <div className="flex gap-1 mb-3 p-1 bg-muted rounded-lg" data-testid="media-tabs">
              <button
                onClick={() => setShowVideo(false)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-all ${!showVideo ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="tab-photos"
              >
                <ImageIcon className="h-4 w-4" /> Fotos ({images.length})
              </button>
              <button
                onClick={() => setShowVideo(true)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-all ${showVideo ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="tab-video"
              >
                <Play className="h-4 w-4" /> Vídeo
              </button>
            </div>
          )}

          {/* Image Gallery */}
          {(!showVideo || !product.video) && (
            <>
              <div className="relative aspect-square rounded-xl overflow-hidden bg-muted border border-border">
                {images.length > 0 ? (
                  <img src={getImageUrl(images[currentImage])} alt={product.title} className="w-full h-full object-contain" />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">Sem imagem</div>
                )}
                {images.length > 1 && (
                  <>
                    <button onClick={() => setCurrentImage(p => (p - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow" data-testid="prev-image-btn">
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button onClick={() => setCurrentImage(p => (p + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow" data-testid="next-image-btn">
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}
                {/* Video badge overlay when not showing video */}
                {product.video && !showVideo && images.length > 0 && (
                  <button
                    onClick={() => setShowVideo(true)}
                    className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-medium hover:bg-black/90 transition-colors backdrop-blur-sm"
                    data-testid="video-overlay-btn"
                  >
                    <Video className="h-3.5 w-3.5" /> Ver vídeo
                  </button>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setCurrentImage(i)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${i === currentImage ? 'border-primary' : 'border-border'}`}>
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Video Player */}
          {showVideo && product.video && (
            <div className="relative rounded-xl overflow-hidden bg-black border border-border" data-testid="video-player-container">
              <video
                src={getVideoUrl(product.video)}
                controls
                controlsList="nodownload"
                playsInline
                className="w-full aspect-video object-contain"
                data-testid="product-video-player"
              >
                Seu navegador não suporta o elemento de vídeo.
              </video>
            </div>
          )}

          {/* Video-only (no images) */}
          {product.video && images.length === 0 && !showVideo && (
            <div
              className="relative aspect-video rounded-xl overflow-hidden bg-black border border-border cursor-pointer group"
              onClick={() => setShowVideo(true)}
              data-testid="video-only-thumbnail"
            >
              <video
                src={getVideoUrl(product.video)}
                className="w-full h-full object-contain"
                muted
                data-testid="product-video-preview"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                <div className="h-16 w-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <Play className="h-7 w-7 text-primary ml-1" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary">{conditionMap[product.condition] || product.condition}</Badge>
              <Badge variant="outline">{typeMap[product.product_type] || product.product_type}</Badge>
              {product.status === 'sold_out' && <Badge variant="destructive">Esgotado</Badge>}
              {product.status !== 'active' && product.status !== 'sold_out' && <Badge variant="destructive">{product.status}</Badge>}
              {isPromoted && (
                <Badge className="bg-gradient-to-r from-amber-400 to-orange-400 text-white border-0 gap-1">
                  <Sparkles className="h-3 w-3" /> Em Destaque
                </Badge>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit' }} data-testid="product-title">
              {product.title}
            </h1>
          </div>

          <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Outfit' }} data-testid="product-price">
            R$ {Number(product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </p>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {product.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {product.location}</span>}
            <span className="flex items-center gap-1"><Eye className="h-4 w-4" /> {product.views || 0} visualizações</span>
            {(product.quantity || 1) > 0 && product.status === 'active' && (
              <span className="flex items-center gap-1"><Package className="h-4 w-4" /> {product.quantity || 1} disponível(eis)</span>
            )}
          </div>

          {/* Delivery Options */}
          {product.delivery_options && product.delivery_options.length > 0 && (
            <div className="p-4 rounded-xl border border-border bg-muted/30" data-testid="delivery-options-display">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Opções de Entrega</p>
              <div className="flex flex-wrap gap-2">
                {product.delivery_options.map(opt => {
                  const info = deliveryMap[opt];
                  if (!info) return null;
                  const Icon = info.icon;
                  return (
                    <span key={opt} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-border text-xs font-medium" data-testid={`delivery-badge-${opt}`}>
                      <Icon className="h-3.5 w-3.5 text-primary" /> {info.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {product.status === 'active' && (!user || user._id !== product.seller_id) && (
            <div className="flex gap-3">
              <Button onClick={handleBuy} className="flex-1 rounded-lg gap-2" disabled={buying} data-testid="buy-btn">
                <ShoppingCart className="h-4 w-4" /> {buying ? 'Comprando...' : 'Comprar agora'}
              </Button>
              <Button variant="outline" onClick={handleChat} className="rounded-lg gap-2" data-testid="chat-seller-btn">
                <MessageCircle className="h-4 w-4" /> Chat
              </Button>
            </div>
          )}

          {product.status === 'sold_out' && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-center" data-testid="sold-out-banner">
              <p className="text-sm font-semibold text-red-600">Produto esgotado</p>
              <p className="text-xs text-red-500 mt-1">Este produto não está mais disponível para compra.</p>
              {user && user._id !== product.seller_id && (
                <Button variant="outline" onClick={handleChat} className="mt-3 rounded-lg gap-2" data-testid="chat-seller-soldout-btn">
                  <MessageCircle className="h-4 w-4" /> Falar com vendedor
                </Button>
              )}
            </div>
          )}

          {/* Seller actions: Edit / Delete */}
          {user && user._id === product.seller_id && (
            <div className="flex gap-3" data-testid="seller-actions">
              <Button variant="outline" className="flex-1 rounded-lg gap-2" onClick={() => navigate(`/edit-listing/${id}`)} data-testid="edit-listing-btn">
                <Pencil className="h-4 w-4" /> Editar Anúncio
              </Button>
              <Button variant="outline" className="rounded-lg gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteDialog(true)} data-testid="delete-listing-btn">
                <Trash2 className="h-4 w-4" /> Remover
              </Button>
            </div>
          )}

          {/* Promote button for seller */}
          {product.status === 'active' && user && user._id === product.seller_id && !isPromoted && (
            <div className="p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50/50" data-testid="promote-section">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                <h3 className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>Destacar Anúncio</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Coloque seu anúncio em destaque na página inicial por 7 dias. Aumente suas chances de venda!
              </p>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handlePromote}
                  disabled={promoting}
                  className="gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg"
                  data-testid="promote-btn"
                >
                  <Sparkles className="h-4 w-4" /> {promoting ? 'Destacando...' : 'Destacar por R$ 10,00'}
                </Button>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Coins className="h-3 w-3" /> Saldo: R$ {(user.credits || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}

          {/* Already promoted */}
          {product.status === 'active' && user && user._id === product.seller_id && isPromoted && (
            <div className="p-4 rounded-xl border border-amber-300 bg-amber-50/50" data-testid="promoted-info">
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <span className="font-medium">Em destaque até {new Date(product.promoted_until).toLocaleDateString('pt-BR')}</span>
              </div>
            </div>
          )}

          <div className="border-t border-border pt-5">
            <h3 className="text-sm font-semibold mb-2">Descrição</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed" data-testid="product-description">
              {product.description}
            </p>
          </div>

          {/* Seller info */}
          {seller && (
            <div className="border-t border-border pt-5">
              <h3 className="text-sm font-semibold mb-3">Vendedor</h3>
              <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-muted/30">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{seller.name}</p>
                  {seller.rating > 0 && (
                    <div className="flex items-center gap-1 text-sm">
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      <span className="font-medium">{seller.rating.toFixed(1)}</span>
                      <span className="text-muted-foreground">({seller.review_count} avaliações)</span>
                    </div>
                  )}
                  {seller.city && <p className="text-xs text-muted-foreground">{seller.city}{seller.state ? `, ${seller.state}` : ''}</p>}
                </div>
              </div>

              {/* Seller Reviews */}
              {sellerReviews.length > 0 && (
                <div className="mt-4 space-y-3" data-testid="seller-reviews">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Avaliações recentes</h4>
                  {sellerReviews.slice(0, 5).map((rev, i) => (
                    <div key={i} className="p-3 rounded-lg border border-border bg-card">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map(n => (
                            <Star key={n} className={`h-3 w-3 ${n <= rev.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/20'}`} />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">{rev.reviewer_name}</span>
                      </div>
                      {rev.comment && <p className="text-xs text-muted-foreground">{rev.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Wise Eagle placeholder */}
          <div className="p-4 rounded-xl border border-dashed border-border bg-muted/30">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>Verificação anti-golpe por Wise Eagle (em breve)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Remover Anúncio</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover o anúncio <span className="font-semibold">&ldquo;{product.title}&rdquo;</span>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialog(false)} disabled={deleting} data-testid="cancel-delete-btn">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="gap-2" data-testid="confirm-delete-btn">
              <Trash2 className="h-4 w-4" /> {deleting ? 'Removendo...' : 'Sim, remover'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
